/**
 * TeamEvalPrsSection: the columns of a team's customer config row that
 * only the eval PR jobs read. The deny list scrubs the customer's client
 * names out of what an eval PR publishes, the generic terms keep the
 * customer's everyday vocabulary off it, and the repository layout says
 * where in the scored benchmark's repository generated tests go.
 *
 * The row is the one the Evals config page edits, saved through the same
 * PUT (useEvalsConfigForm). Its eval group is a required column, so a
 * team with no row is sent there to create it first. The repository is
 * read back from the scored benchmark, since that page is where it is
 * picked.
 */

import { getApiErrorDetail } from '@/api/client'
import { useGetTeamEvalsConfigApiInternalTeamsTeamIdEvalsConfigGet } from '@/api/generated/internal/internal'
import type { TeamEvalsConfigResponse } from '@/api/generated/model'
import { SettingsPageHeader } from '@/components/settings/SettingsPageHeader'
import { SettingsBlock, SettingsSection } from '@/components/settings/SettingsSection'
import { EYEBROW } from '@/components/ui/eyebrow'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { useEvalsConfigForm } from '@/hooks/useEvalsConfigForm'

import { DenyTermsEditor } from './DenyTermsEditor'
import { scoredBenchmark } from './evalsConfigForm'
import { EvalsConfigFooter } from './EvalsConfigFooter'
import type { TeamConfigSectionProps } from './sections'
import { TextField } from './TextField'

export function TeamEvalPrsSection({
  section,
  team,
}: TeamConfigSectionProps): React.JSX.Element {
  const setup = useGetTeamEvalsConfigApiInternalTeamsTeamIdEvalsConfigGet(team.id)
  const loaded = setup.data?.status === 200 ? setup.data.data : undefined
  return (
    <div>
      <SettingsPageHeader section={section} />
      {setup.isLoading ? (
        <SettingsBlock>
          <Skeleton className="h-24 w-full" />
        </SettingsBlock>
      ) : setup.isError ? (
        <SettingsBlock>
          <p className="text-sm text-destructive" role="alert">
            {getApiErrorDetail(setup.error) ?? 'Failed to load the evals config.'}
          </p>
        </SettingsBlock>
      ) : loaded === undefined ? null : loaded.config === null ? (
        <SettingsBlock>
          <p className="text-sm text-muted-foreground">
            This team has no evals config yet. Create one on the Evals config page first.
          </p>
        </SettingsBlock>
      ) : (
        <EvalPrsForm teamId={team.id} setup={loaded} />
      )}
    </div>
  )
}

function EvalPrsForm({
  teamId,
  setup,
}: {
  teamId: string
  setup: TeamEvalsConfigResponse
}): React.JSX.Element {
  // No adapter list: this page sends the benchmarks back as saved, and
  // the api checks their routes on the save.
  const state = useEvalsConfigForm(teamId, setup, [])
  const { form, update } = state
  // The scored repository, tests_root, tests_subdir and the scored
  // adapter's case file rule decide where reconcile looks for a merged
  // test, and a published batch keeps no copy of them. Moving one after
  // the fact makes a merge read as a deleted test, so once the layout is
  // saved they are frozen, and the api refuses a later change.
  const savedLayout = setup.config?.tests_root != null
  const scored = scoredBenchmark(form)
  const repo = scored?.source.kind === 'github' ? scored.source.repo.trim() : ''

  return (
    <form onSubmit={state.submit}>
      <SettingsSection
        title="Deny list"
        description="Eval PRs scrub the customer's own client and counterparty names out of everything they publish. Turning on this team's jobs under Evals config needs these names, or the acknowledgment that there are none to list."
      >
        <div className="space-y-6">
          <DenyTermsEditor
            terms={form.standing_deny_terms}
            onChange={(next) => {
              state.edit((previous) => ({
                ...previous,
                standing_deny_terms: next(previous.standing_deny_terms),
              }))
            }}
          />
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <Switch
                id="deny-ack"
                checked={form.ack_empty_standing_terms}
                onCheckedChange={(checked) => { update('ack_empty_standing_terms', checked) }}
              />
              <Label htmlFor="deny-ack">Run with an empty deny list</Label>
            </div>
            <p className="text-xs text-muted-foreground">
              Turn this on only when the customer has no client names to scrub. Eval PRs then
              publish without this leg of the deny list.
            </p>
          </div>
        </div>
      </SettingsSection>

      <SettingsSection
        title="Generic terms"
        description="These are the customer's everyday words that never name a client (Dry Van, FedEx). The deny list skips a word found in a trajectory when it matches one of these exactly. Client names in the deny list above are scrubbed either way."
      >
        <div className="max-w-xl space-y-2">
          <Textarea
            id="generic-terms"
            aria-label="Generic terms"
            aria-describedby="generic-terms-hint"
            value={form.generic_terms}
            rows={10}
            placeholder={'Dry Van\nFedEx'}
            onChange={(e) => { update('generic_terms', e.target.value) }}
          />
          <p id="generic-terms-hint" className="text-xs text-muted-foreground">
            List one term per line. Letter case does not matter. The next nightly run also drops an
            added term from every deny list already built for an episode in progress. Removing the
            term later does not put it back on those lists.
          </p>
        </div>
      </SettingsSection>

      <SettingsSection
        title="Repository layout"
        description="These describe the repository eval PRs publish into. Leave the tests directory, the generated tests subdirectory and the review link base empty for a team that gets no eval PRs or generated variants."
      >
        <div className="max-w-xl space-y-4">
          <div className="space-y-1">
            <p className={EYEBROW}>Repository</p>
            {repo === '' ? (
              <p className="text-sm text-muted-foreground">
                None. Score a GitHub benchmark on the Evals config page first.
              </p>
            ) : (
              <p className="break-all font-mono text-xs">{repo}</p>
            )}
          </div>
          <TextField
            id="evals-tests-root"
            label="Tests directory"
            value={form.tests_root}
            mono
            disabled={savedLayout}
            placeholder="src/server/agents/Agent/__tests__"
            hint={
              savedLayout
                ? 'It was set when the layout was saved. Moving it would strand the tests an open eval PR was published against.'
                : 'Eval PRs and generated variants read the tests under this repo-relative directory.'
            }
            onChange={(value) => { update('tests_root', value) }}
          />
          <TextField
            id="evals-tests-subdir"
            label="Generated tests subdirectory"
            value={form.tests_subdir}
            mono
            disabled={savedLayout}
            placeholder="benchmark"
            hint={
              savedLayout
                ? 'It was set when the layout was saved, for the same reason as the tests directory.'
                : 'It sits under the tests directory. Eval PRs write new tests here.'
            }
            onChange={(value) => { update('tests_subdir', value) }}
          />
          <TextField
            id="evals-review-base"
            label="Review link base"
            value={form.review_base_url}
            mono
            placeholder="https://acme.trainloop.ai"
            hint="Eval PR bodies link back to the trajectory a generated test came from. This is the host those links point at."
            onChange={(value) => { update('review_base_url', value) }}
          />
          <TextField
            id="evals-registry"
            label="Benchmark index file"
            value={form.registry_path}
            mono
            placeholder="scripts/utils/benchmarkTestRegistry.ts"
            hint="Optional. The file that lists benchmark tests in this repository. Eval PRs add each new test to it. Leave it empty when the repository has no such file."
            onChange={(value) => { update('registry_path', value) }}
          />
        </div>
      </SettingsSection>

      <EvalsConfigFooter state={state} />
    </form>
  )
}
